import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { v4 as uuidv4 } from "uuid";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { useCreateApiKeyMutation } from "@/services/backend/api-key/service";
import SecretField from "@/shared/components/domain/secrets/secret-field";
import { useState } from "react";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";

const FormSchema = z.object({
  exp: z.string(),
});

interface CreateApiKeyFormProps {
  customerId: string;
}

export function CreateApiKeyForm({ customerId }: CreateApiKeyFormProps) {
  const [isOpen, setIsOpen] = useState(false); // Modal-Open-Zustand
  const [createApiKey, result] = useCreateApiKeyMutation();
  const { isSuccess, reset, data: responseData, error } = result;

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
  });

  const handleClose = () => {
    if (isOpen) {
      reset();
      form.reset();
      form.resetField("exp");
    }
    setIsOpen(!isOpen);
  };

  function onSubmit(data: z.infer<typeof FormSchema>) {
    const validTime = Number(data.exp) * 365 * 24 * 60 * 60;
    const currentTimestamp = Math.floor(Date.now() / 1000);
    const timestamp = currentTimestamp + validTime;
    createApiKey({
      id: uuidv4(),
      customerId,
      exp: timestamp,
      active: true,
    });
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button variant="default">Neuen API-Key erstellen</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Key anlegen</DialogTitle>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <div className="flex items-center space-x-2">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="w-full space-y-6"
            >
              <FormField
                control={form.control}
                name="exp"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Laufzeit</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        placeholder="Laufzeit in Jahren"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>Laufzeit in Jahren</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {!isSuccess && (
                <Button disabled={isSuccess} type="submit">
                  Submit
                </Button>
              )}
            </form>
          </Form>
          {isSuccess && <SecretField secret={responseData.result} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
